import { useResourceRoutePreview } from '@/shared/components/resource-route-preview/context';
import { PERMISSIONS, PermissionTuple } from '@/shared/permissions';

import { getAccessState, useCASL } from './useCASL';

export type PermissionedResource =
    'dashboards' | 'folders' | 'investments' | 'lifecycles' | 'lists' | 'memos' | 'pipelines' | 'teams';

export type PermissionedAction = 'read' | 'create' | 'update' | 'comment' | 'invite' | 'manage';

const resourcePermissionMap: Record<PermissionedResource, Partial<Record<PermissionedAction, PermissionTuple>>> = {
    dashboards: {
        create: PERMISSIONS.DASHBOARD.CREATE,
        read: PERMISSIONS.DASHBOARD.READ,
        update: PERMISSIONS.DASHBOARD.UPDATE,
        comment: PERMISSIONS.DASHBOARD.COMMENT,
        invite: PERMISSIONS.DASHBOARD.INVITE,
        manage: PERMISSIONS.DASHBOARD.MANAGE,
    },
    folders: {
        create: PERMISSIONS.FOLDER.CREATE,
        read: PERMISSIONS.FOLDER.READ,
        update: PERMISSIONS.FOLDER.UPDATE,
        comment: PERMISSIONS.FOLDER.COMMENT,
        invite: PERMISSIONS.FOLDER.INVITE,
        manage: PERMISSIONS.FOLDER.MANAGE,
    },
    investments: {
        create: PERMISSIONS.INVESTMENT.CREATE,
        read: PERMISSIONS.INVESTMENT.READ,
        update: PERMISSIONS.INVESTMENT.UPDATE,
        comment: PERMISSIONS.INVESTMENT.COMMENT,
        invite: PERMISSIONS.INVESTMENT.INVITE,
        manage: PERMISSIONS.INVESTMENT.MANAGE,
    },
    lifecycles: {
        create: PERMISSIONS.LIFECYCLE.CREATE,
        read: PERMISSIONS.LIFECYCLE.READ,
        update: PERMISSIONS.LIFECYCLE.UPDATE,
        comment: PERMISSIONS.LIFECYCLE.COMMENT,
        invite: PERMISSIONS.LIFECYCLE.INVITE,
        manage: PERMISSIONS.LIFECYCLE.MANAGE,
    },
    lists: {
        create: PERMISSIONS.LIST.CREATE,
        read: PERMISSIONS.LIST.READ,
        update: PERMISSIONS.LIST.UPDATE,
        comment: PERMISSIONS.LIST.COMMENT,
        invite: PERMISSIONS.LIST.INVITE,
        manage: PERMISSIONS.LIST.MANAGE,
    },
    memos: {
        create: PERMISSIONS.MEMO.CREATE,
        read: PERMISSIONS.MEMO.READ,
        update: PERMISSIONS.MEMO.UPDATE,
        comment: PERMISSIONS.MEMO.COMMENT,
        invite: PERMISSIONS.MEMO.INVITE,
        manage: PERMISSIONS.MEMO.MANAGE,
    },
    pipelines: {
        create: PERMISSIONS.PIPELINE.CREATE,
        read: PERMISSIONS.PIPELINE.READ,
        update: PERMISSIONS.PIPELINE.UPDATE,
        comment: PERMISSIONS.PIPELINE.COMMENT,
        invite: PERMISSIONS.PIPELINE.INVITE,
        manage: PERMISSIONS.PIPELINE.MANAGE,
    },
    teams: {
        create: PERMISSIONS.TEAM.CREATE,
        read: PERMISSIONS.TEAM.READ,
        update: PERMISSIONS.TEAM.UPDATE,
        manage: PERMISSIONS.TEAM.MANAGE,
    },
};

const deniedAccessState = {
    allowed: false,
    hidden: true,
    disabled: true,
    readOnly: true,
    canRead: false,
    canInteract: false,
    canMutate: false,
    reason: 'INSUFFICIENT_PERMISSIONS',
} as const;

export const getResourceActionPermission = (resource: PermissionedResource, action: PermissionedAction) =>
    resourcePermissionMap[resource]?.[action];

export const useResourceActionAccess = (
    resource: PermissionedResource | string | undefined,
    action: PermissionedAction,
    resourceId?: string | null,
) => {
    const { ability } = useCASL();
    const preview = useResourceRoutePreview();
    const permission = resource ? getResourceActionPermission(resource as PermissionedResource, action) : undefined;

    if (!permission) {
        return deniedAccessState;
    }
    if (preview && action !== 'read') {
        return { ...deniedAccessState, reason: 'READ_ONLY_RESOURCE_PREVIEW' };
    }

    return getAccessState(
        ability,
        permission,
        resourceId
            ? {
                  instance: { id: resourceId },
              }
            : undefined,
    );
};
